/**
 * Where people struggle (R10 step 1, the Friction screen): every refused request the browser
 * sees becomes `request.failed` with the status, the method and the API path *pattern*
 * (openapi-fetch's schema path, `/company/people/{userId}`, never the real path), and every
 * uncaught error becomes `browser.error` with a short message. No payloads, no field values.
 */
import type { PaigeClient } from '@paige/api-client';
import { track } from './events';

/** openapi-fetch's middleware shape, taken from the client so the app needs no dependency of its own. */
type Middleware = Parameters<PaigeClient['use']>[0];
import { currentScreen } from './screens';

const MAX_MESSAGE = 120;

/** The open screen as an option, or nothing before the router has resolved one. */
function onScreen(): { screen?: string } {
  const screen = currentScreen();
  return screen ? { screen } : {};
}

/** Problem-details titles are short words like `NameTaken`; anything longer or absent is dropped. */
async function titleOf(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.clone().json()) as { title?: unknown };
    return typeof body.title === 'string' && body.title.length <= 60 ? body.title : undefined;
  } catch {
    return undefined;
  }
}

/** The middleware to `client.use`: records refused answers and lets them through untouched. */
export function frictionMiddleware(): Middleware {
  return {
    async onResponse({ request, response, schemaPath }) {
      if (response.status < 400 || schemaPath === '/events' || schemaPath === '/client-errors')
        return response;
      // A 401 means the session is over (or never was): the app signs the person out, which is
      // the design, not a struggle; the front door's /me before sign-in is the common case.
      if (response.status === 401) return response;
      const title = await titleOf(response);
      track('request.failed', {
        feature: 'request.failed',
        ...onScreen(),
        detail: {
          status: response.status,
          method: request.method,
          path: schemaPath,
          ...(title ? { title } : {}),
        },
      });
      return response;
    },
  };
}

function shortMessage(reason: unknown): string {
  const text =
    reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason ?? 'unknown');
  return text.slice(0, MAX_MESSAGE);
}

/** Uncaught errors and rejections, as one event each; returns the unsubscribe. */
export function watchBrowserErrors(win: Window = window): () => void {
  const onError = (e: ErrorEvent) =>
    track('browser.error', {
      feature: 'browser.error',
      ...onScreen(),
      detail: { kind: 'error', message: shortMessage(e.error ?? e.message) },
    });
  const onRejection = (e: PromiseRejectionEvent) =>
    track('browser.error', {
      feature: 'browser.error',
      ...onScreen(),
      detail: { kind: 'rejection', message: shortMessage(e.reason) },
    });
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
  };
}
